import { AvatarDropdown, AvatarName, Footer } from '@/components';
import { fetchUser } from '@/services/user';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import type { RunTimeLayoutConfig } from '@umijs/max';
import { history } from '@umijs/max';
import React, { Suspense } from 'react';
import '@ant-design/v5-patch-for-react-19';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';
import AuthWrap from './components/AuthWrap';

const QuickList = React.lazy(() => import('@/pages/emergency/QuickList'));
const loginPath = '/login';

/**
 * @see  https://umijs.org/zh-CN/plugins/plugin-initial-state
 * */
export async function getInitialState(): Promise<{
  quickVisible?: boolean;
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: (params?: any) => Promise<API.CurrentUser | undefined>;
}> {
  const fetchUserInfo = async (params?: any) => {
    try {
      const res = await fetchUser({
        skipErrorHandler: true,
        params,
      });
      return res.content;
    } catch (error) {
      history.push(loginPath);
    }
    return undefined;
  };
  // 如果不是登录页面，执行
  const { location } = history;
  if (location.pathname !== loginPath) {
    const currentUser = await fetchUserInfo();
    return {
      fetchUserInfo,
      currentUser,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  return {
    fetchUserInfo,
    quickVisible: false,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({ initialState }) => {
  return {
    actionsRender: () => null,
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown menu>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: {
      content: initialState?.currentUser?.name,
    },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，重定向到 login
      if (!initialState?.currentUser && location.pathname !== loginPath) {
        history.push(loginPath);
      }
    },
    bgLayoutImgList: [
      {
        src: '/images/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr.webp',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: '/images/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr.webp',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: '/images/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr.webp',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: [],
    menuHeaderRender: undefined,
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          {children}
          <AuthWrap key="emergencyQuick" permission="emergencyQuick">
            <Suspense fallback={null}>
              <QuickList />
            </Suspense>
          </AuthWrap>
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request = {
  headers: {
    'Prefix-Url': window.location.origin,
  },
  ...errorConfig,
};
